import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import Label from '@/components/common/Label';
import LabelChipMini from '@/components/common/LabelChipMini';
import StatusInfo from '@/components/common/StatusInfo';
import Title from '@/components/common/Title';
import CuttingForm from '@/components/ForestMassDetail/CuttingForm';
import PlotInventoriesUploadForm from '@/components/InventoryPlotUploadForm';
import UavPointCloudButton from '@/components/PointCloudsUploadForm/uav';
import type {ForestMass} from '@/domain/entities/ForestMass';
import type {ForestMassStatus} from '@/domain/entities/ForestMassStatus';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import type {CreateInventoryPlot, CutForestMass} from '@/domain/entities/Op';
import type {UavPointCloud, UavPointCloudCreate} from '@/domain/entities/UavPointCloud';
import type BusinessLogicBasedOnStatuses from '@/domain/useCases/businessLogicBasedOnStatuses';
import type UserPermissions from '@/domain/useCases/userPermissions';

//TYPES
type Props = {
  permissions: UserPermissions;
  businessLogic: BusinessLogicBasedOnStatuses;
  forestMass: ForestMass;
  forestMassStatus: ForestMassStatus;
  uavPointCloud?: UavPointCloud;
  lidarTechnologies?: LidarTechnology[];
  onCutForestMass: (domain: CutForestMass) => void;
  onUploadUavPointCloud: (domain: Partial<UavPointCloudCreate>, file: File) => void;
  onUploadInventoryPlot?: (domain: CreateInventoryPlot, file: File) => void;
  onDeleteUavPointCloud: (pointCloudUuid: string) => void;
  onDownloadUavPointCloud: (pointCloudUuid: string) => void;
  onReuploadUavPointCloud: (pointCloudUuid: string, file: File) => void;
};

const ForestMassStatuses: FC<Props> = ({
  permissions,
  businessLogic,
  forestMass,
  forestMassStatus,
  uavPointCloud,
  lidarTechnologies,
  onCutForestMass,
  onUploadUavPointCloud,
  onUploadInventoryPlot,
  onDeleteUavPointCloud,
  onDownloadUavPointCloud,
  onReuploadUavPointCloud,
}) => {
  const {t} = useTranslation();

  const {
    finsaStatus,
    finsaStatusChangeDate,
    aseforStatus,
    aseforStatusChangeDate,
    fortopStatus,
    fortopStatusChangeDate,
    uavMetricsStatus,
    uavMetricsStatusChangeDate,
  } = forestMassStatus;

  const handleUploadInventoryPlot = (file?: File) =>
    file && onUploadInventoryPlot?.({forestMassUuid: forestMass.uuid}, file);

  const fixedRealWeight = forestMass.realWeight?.toFixed(2);

  return (
    <>
      <Title size="h6" label={t('status.title')} />
      <Stack direction="row">
        <Label value="FINSA" direction="row">
          <Stack direction="row" spacing={1} sx={{alignItems: 'flex-start'}}>
            <StatusInfo id={finsaStatus} date={finsaStatusChangeDate} />
            {forestMass.isCut && forestMass.realWeight != null && (
              <LabelChipMini icon={ScaleOutlinedIcon} value={`${fixedRealWeight} Tn`} />
            )}
          </Stack>
          {permissions.hasPermissionToCutForestMass() && (
            <CuttingForm forestMass={forestMass} onCutForestMass={onCutForestMass} />
          )}
        </Label>
      </Stack>
      <Divider sx={{m: 2}} variant="fullWidth" />
      <Label value="ASEFOR" direction="row">
        <StatusInfo id={aseforStatus} date={aseforStatusChangeDate} />
        {permissions.hasPermissionToUploadPlotInventories() && onUploadInventoryPlot && (
          <PlotInventoriesUploadForm
            onUploadInventoryPlot={handleUploadInventoryPlot}
            disabled={businessLogic.isForestMassCut()}
          />
        )}
      </Label>
      <Divider sx={{m: 2}} variant="fullWidth" />
      <Label value="FORTOP" direction="row">
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          <StatusInfo id={fortopStatus} date={fortopStatusChangeDate} />
          {uavMetricsStatus && uavMetricsStatusChangeDate && (
            <StatusInfo id={uavMetricsStatus} date={uavMetricsStatusChangeDate} />
          )}
        </Stack>
        <Stack direction="row">
          <UavPointCloudButton
            canUpload={permissions.hasPermissionToUploadUavCloudPoints()}
            canDelete={permissions.hasPermissionToDeleteUavCloudPoints()}
            lidarTechnologies={lidarTechnologies}
            uavPointCloud={uavPointCloud}
            onDelete={onDeleteUavPointCloud}
            onUpload={onUploadUavPointCloud}
            onDownload={onDownloadUavPointCloud}
            onReupload={onReuploadUavPointCloud}
            disabled={businessLogic.isForestMassCut()}
          />
        </Stack>
      </Label>
    </>
  );
};
export default ForestMassStatuses;
